import { useState, useMemo } from "react";

// ── Rates ─────────────────────────────────────────────────────────────────────
const RATES = [
  { label: "Safe",       pct: 4,  sub: "4% · High-yield savings" },
  { label: "Balanced",   pct: 7,  sub: "7% · Stock market avg"   },
  { label: "Aggressive", pct: 10, sub: "10% · Growth stocks"     },
];

const HORIZONS = [5, 10, 20, 30];

// ── Account detail per rate ────────────────────────────────────────────────────
const ACCOUNT_INFO = [
  {
    icon: "🏦",
    name: "High-Yield Savings Account",
    tag: "HYSA",
    color: "#0ea5e9",
    bgColor: "#f0f9ff",
    borderColor: "#bae6fd",
    tagColor: "#0369a1",
    description: "A savings account that pays WAY more interest than a regular bank. Your money is 100% safe — guaranteed by the US government up to $250k (FDIC insured).",
    bestFor: "Emergency fund or saving for something in 1–3 years",
    safety: "Very Safe",
    safetyColor: "#16a34a",
    safetyBg: "#dcfce7",
    where: [
      { name: "Ally Bank",  note: "No minimum, fully online" },
      { name: "Marcus",     note: "By Goldman Sachs, easy setup" },
      { name: "SoFi",       note: "Great for beginners, free" },
    ],
  },
  {
    icon: "📊",
    name: "S&P 500 Index Fund",
    tag: "Index Fund",
    color: "#10b981",
    bgColor: "#f0fdf4",
    borderColor: "#bbf7d0",
    tagColor: "#065f46",
    description: "Invests in 500 of America's biggest companies (Apple, Amazon, Google…) all at once. Historically grows ~10%/year — we use 7% to be realistic after inflation.",
    bestFor: "Money you won't need for 5+ years. Has bad years but ALWAYS recovers.",
    safety: "Medium Risk",
    safetyColor: "#d97706",
    safetyBg: "#fef9c3",
    where: [
      { name: "Fidelity", note: "No minimums, zero fees" },
      { name: "Vanguard", note: "The original index fund creator" },
      { name: "Schwab",   note: "Great app + free trades" },
    ],
  },
  {
    icon: "🚀",
    name: "Growth ETFs / Stocks",
    tag: "Growth",
    color: "#8b5cf6",
    bgColor: "#faf5ff",
    borderColor: "#e9d5ff",
    tagColor: "#6d28d9",
    description: "Invests in fast-growing companies (tech, AI, biotech). Can grow faster but drops harder during bad markets. Think Nasdaq (QQQ) or individual stocks.",
    bestFor: "Money you can leave alone for 10+ years. More patience = more reward.",
    safety: "Higher Risk",
    safetyColor: "#dc2626",
    safetyBg: "#fee2e2",
    where: [
      { name: "Fidelity",   note: "Great research tools, no fees" },
      { name: "Schwab",     note: "No minimums, solid app" },
      { name: "Robinhood",  note: "Simple app, good for first timers" },
    ],
  },
];

// ── Math ──────────────────────────────────────────────────────────────────────
function calcValues(start: number, weekly: number, ratePct: number, years: number): number[] {
  const r = ratePct / 100 / 52;
  const vals: number[] = [start];
  for (let y = 1; y <= years; y++) {
    const w  = y * 52;
    const fv = r === 0
      ? start + weekly * w
      : start * Math.pow(1 + r, w) + weekly * ((Math.pow(1 + r, w) - 1) / r);
    vals.push(Math.round(fv));
  }
  return vals;
}

function calcDelayed(start: number, weekly: number, ratePct: number, years: number, delay: number): number[] {
  const r = ratePct / 100 / 52;
  const vals: number[] = [];
  for (let y = 0; y <= years; y++) {
    const wTotal   = y * 52;
    const fvStart  = r === 0 ? start : start * Math.pow(1 + r, wTotal);
    const wContrib = Math.max(0, y - delay) * 52;
    const fvContrib = wContrib === 0 ? 0 : r === 0
      ? weekly * wContrib
      : weekly * ((Math.pow(1 + r, wContrib) - 1) / r);
    vals.push(Math.round(fvStart + fvContrib));
  }
  return vals;
}

// ── Formatters ────────────────────────────────────────────────────────────────
function fmt(n: number)      { return "$" + Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 }); }
function fmtShort(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${Math.round(n / 1_000)}k`;
  return `$${n}`;
}

// ── SVG Chart ─────────────────────────────────────────────────────────────────
const CW = 300, CH = 170;
const PL = 48, PR = 12, PT = 12, PB = 26;
const IW = CW - PL - PR, IH = CH - PT - PB;

function tx(y: number, yrs: number) { return PL + (y / yrs) * IW; }
function ty(v: number, mx: number)  { return PT + IH - (v / mx) * IH; }

function Chart({ main, compare, years }: { main: number[]; compare: number[] | null; years: number }) {
  const maxVal  = Math.max(...main, ...(compare ?? [0])) * 1.08 || 1;
  const xStep   = years <= 5 ? 1 : years <= 10 ? 2 : 5;
  const yTicks  = [0, 0.25, 0.5, 0.75, 1.0].map(f => maxVal * f);

  const mkPts = (vals: number[]) =>
    vals.map((v, i) => `${tx(i, years).toFixed(1)},${ty(v, maxVal).toFixed(1)}`).join(" ");

  const areaPath =
    `M ${tx(0, years).toFixed(1)},${(PT + IH).toFixed(1)} ` +
    main.map((v, i) => `L ${tx(i, years).toFixed(1)},${ty(v, maxVal).toFixed(1)}`).join(" ") +
    ` L ${tx(years, years).toFixed(1)},${(PT + IH).toFixed(1)} Z`;

  return (
    <svg viewBox={`0 0 ${CW} ${CH}`} width="100%" style={{ overflow: "visible", display: "block" }}>
      <defs>
        <linearGradient id="wifGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#10b981" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#10b981" stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {yTicks.map((v, i) => (
        <g key={i}>
          <line x1={PL} y1={ty(v, maxVal)} x2={CW - PR} y2={ty(v, maxVal)}
            stroke={i === 0 ? "#d1d5db" : "#f3f4f6"} strokeWidth="1" />
          <text x={PL - 4} y={ty(v, maxVal) + 3.5} textAnchor="end"
            fontSize="7.5" fill="#9ca3af" fontFamily="Nunito,sans-serif" fontWeight="700">
            {fmtShort(Math.round(v))}
          </text>
        </g>
      ))}

      {Array.from({ length: Math.floor(years / xStep) + 1 }, (_, i) => i * xStep).map(y => (
        <text key={y} x={tx(y, years)} y={CH - 5} textAnchor="middle"
          fontSize="7.5" fill="#9ca3af" fontFamily="Nunito,sans-serif" fontWeight="700">
          {y === 0 ? "Now" : `${y}yr`}
        </text>
      ))}

      <path d={areaPath} fill="url(#wifGrad)" />

      {compare && (
        <>
          <polyline points={mkPts(compare)} fill="none"
            stroke="#ef4444" strokeWidth="2.5" strokeDasharray="5,3"
            strokeLinecap="round" strokeLinejoin="round" />
          <circle cx={tx(years, years)} cy={ty(compare[compare.length - 1], maxVal)}
            r="4" fill="#ef4444" stroke="#fff" strokeWidth="2" />
        </>
      )}

      <polyline points={mkPts(main)} fill="none"
        stroke="#10b981" strokeWidth="3"
        strokeLinecap="round" strokeLinejoin="round" />

      <circle cx={tx(years, years)} cy={ty(main[main.length - 1], maxVal)}
        r="5" fill="#10b981" stroke="#fff" strokeWidth="2" />
    </svg>
  );
}

// ── Where to Put It card ───────────────────────────────────────────────────────
function WhereToInvest({ rateIdx, weeklyAmt, years }: { rateIdx: number; weeklyAmt: number; years: number }) {
  const info = ACCOUNT_INFO[rateIdx];
  return (
    <div style={{ background: info.bgColor, border: `2px solid ${info.borderColor}`, borderRadius: 20, padding: "16px", marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 22 }}>{info.icon}</span>
        <div>
          <div style={{ fontSize: 14, fontWeight: 900, color: "#1f2937", fontFamily: "Fredoka One" }}>{info.name}</div>
          <span style={{ background: info.color, color: "#fff", fontSize: 10, fontWeight: 800, borderRadius: 6, padding: "2px 7px" }}>{info.tag}</span>
        </div>
        <div style={{ marginLeft: "auto", background: info.safetyBg, borderRadius: 8, padding: "4px 8px", textAlign: "center" as const }}>
          <div style={{ fontSize: 10, fontWeight: 900, color: info.safetyColor }}>{info.safety}</div>
        </div>
      </div>

      <p style={{ fontSize: 12, color: "#374151", fontWeight: 700, margin: "0 0 10px", lineHeight: 1.5 }}>
        {info.description}
      </p>

      <div style={{ background: "#ffffff90", borderRadius: 12, padding: "8px 12px", marginBottom: 10 }}>
        <div style={{ fontSize: 10, fontWeight: 900, color: "#6b7280", letterSpacing: 0.8, marginBottom: 2 }}>BEST FOR</div>
        <div style={{ fontSize: 12, color: "#374151", fontWeight: 700 }}>✅ {info.bestFor}</div>
      </div>

      <div style={{ fontSize: 11, fontWeight: 900, color: "#6b7280", letterSpacing: 0.8, marginBottom: 8 }}>WHERE TO OPEN ONE (FREE)</div>
      <div style={{ display: "flex", flexDirection: "column" as const, gap: 6 }}>
        {info.where.map((w, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, background: "#fff", borderRadius: 10, padding: "8px 12px", border: `1.5px solid ${info.borderColor}` }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: info.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <span style={{ fontSize: 14 }}>🏛</span>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 900, color: "#1f2937" }}>{w.name}</div>
              <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 700 }}>{w.note}</div>
            </div>
          </div>
        ))}
      </div>

      {weeklyAmt > 0 && (
        <div style={{ marginTop: 10, background: info.color, borderRadius: 12, padding: "8px 12px" }}>
          <div style={{ fontSize: 12, color: "#fff", fontWeight: 800 }}>
            💡 Saving ${weeklyAmt}/week = ${(weeklyAmt * 52).toLocaleString()}/year going into your {info.tag}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Growth Milestones card ─────────────────────────────────────────────────────
function GrowthMilestones({ mainValues, startAmt, weeklyAmt, years, ageNum }: {
  mainValues: number[]; startAmt: number; weeklyAmt: number; years: number; ageNum: number;
}) {
  const milestoneYears = years <= 5
    ? [1, Math.floor(years / 2), years].filter((v, i, a) => a.indexOf(v) === i && v <= years && v > 0)
    : years <= 10
    ? [1, 5, years]
    : years <= 20
    ? [1, 5, 10, years]
    : [1, 5, 10, 20, years].filter(v => v <= years);

  const rows = milestoneYears.map(y => {
    const total = mainValues[y] ?? 0;
    const contributed = Math.round(startAmt + weeklyAmt * 52 * y);
    const interest = Math.max(0, total - contributed);
    const interestPct = total > 0 ? Math.round((interest / total) * 100) : 0;
    return { y, total, contributed, interest, interestPct };
  });

  return (
    <div style={{ background: "#fff", border: "2px solid #e5e7eb", borderRadius: 20, padding: "16px", marginBottom: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 900, color: "#1f2937", fontFamily: "Fredoka One", marginBottom: 12 }}>
        📅 How Your Money Grows Year by Year
      </div>

      <div style={{ display: "flex", flexDirection: "column" as const, gap: 8 }}>
        {rows.map(({ y, total, contributed, interest, interestPct }) => (
          <div key={y} style={{ background: "#f9fafb", borderRadius: 14, padding: "10px 12px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <div>
                <span style={{ fontSize: 13, fontWeight: 900, color: "#374151" }}>
                  Year {y}
                </span>
                {ageNum > 0 && (
                  <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 700, marginLeft: 6 }}>
                    (age {ageNum + y})
                  </span>
                )}
              </div>
              <span style={{ fontSize: 16, fontWeight: 900, color: "#10b981", fontFamily: "Fredoka One" }}>
                {fmt(total)}
              </span>
            </div>

            {/* Stacked bar: contributions vs interest */}
            <div style={{ height: 8, borderRadius: 6, background: "#e5e7eb", overflow: "hidden", marginBottom: 5 }}>
              <div style={{ display: "flex", height: "100%" }}>
                <div style={{ width: `${100 - interestPct}%`, background: "#6b7280", borderRadius: "6px 0 0 6px" }} />
                <div style={{ width: `${interestPct}%`, background: "#10b981", borderRadius: "0 6px 6px 0" }} />
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <div style={{ width: 8, height: 8, borderRadius: 2, background: "#6b7280" }} />
                <span style={{ fontSize: 10, color: "#6b7280", fontWeight: 700 }}>You put in {fmt(contributed)}</span>
              </div>
              {interest > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: "#10b981" }} />
                  <span style={{ fontSize: 10, color: "#10b981", fontWeight: 800 }}>+{fmt(interest)} FREE 🎉</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 10, fontSize: 11, color: "#6b7280", fontWeight: 700, textAlign: "center" as const }}>
        The green bar grows bigger every year — that's compound interest working for you!
      </div>
    </div>
  );
}

// ── Main screen ───────────────────────────────────────────────────────────────
export default function WhatIfScreen() {
  const [startInput,  setStartInput]  = useState("0");
  const [weeklyInput, setWeeklyInput] = useState("10");
  const [rateIdx,     setRateIdx]     = useState(1);
  const [years,       setYears]       = useState(10);
  const [showCompare, setShowCompare] = useState(false);
  const [ageInput,    setAgeInput]    = useState("13");

  const startAmt  = Math.max(0, parseFloat(startInput)  || 0);
  const weeklyAmt = Math.max(0, parseFloat(weeklyInput) || 0);
  const rate      = RATES[rateIdx].pct;
  const ageNum    = parseInt(ageInput) || 0;

  const delayYears = years <= 5 ? 0 : years <= 10 ? 5 : 10;
  const canCompare = delayYears > 0;

  const mainValues = useMemo(
    () => calcValues(startAmt, weeklyAmt, rate, years),
    [startAmt, weeklyAmt, rate, years]
  );
  const compareValues = useMemo(
    () => (showCompare && canCompare) ? calcDelayed(startAmt, weeklyAmt, rate, years, delayYears) : null,
    [showCompare, canCompare, startAmt, weeklyAmt, rate, years, delayYears]
  );

  const finalValue    = mainValues[mainValues.length - 1];
  const totalInvested = Math.round(startAmt + weeklyAmt * 52 * years);
  const interestEarned = Math.max(0, finalValue - totalInvested);
  const interestPct   = finalValue > 0 ? Math.round((interestEarned / finalValue) * 100) : 0;

  const compareFinal  = compareValues ? compareValues[compareValues.length - 1] : 0;
  const headStart     = finalValue - compareFinal;

  const inpStyle: any = {
    width: "100%", border: "2px solid #e5e7eb", borderRadius: 12,
    padding: "10px 14px", fontSize: 20, fontFamily: "Fredoka One",
    fontWeight: 900, boxSizing: "border-box", color: "#10b981",
    outline: "none", textAlign: "center" as const,
  };

  return (
    <div style={{ fontFamily: "Nunito", paddingBottom: 100 }}>

      {/* Header */}
      <div style={{ background: "linear-gradient(135deg,#10b981,#0284c7)", padding: "20px 18px 18px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -20, right: -10, fontSize: 100, opacity: 0.1 }}>📈</div>
        <div style={{ fontSize: 26, fontWeight: 900, color: "#fff", fontFamily: "Fredoka One", marginBottom: 4 }}>📈 What If?</div>
        <div style={{ fontSize: 13, color: "#ffffffcc", fontWeight: 700, marginBottom: 14 }}>
          See what your money could grow into
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#ffffff20", borderRadius: 14, padding: "8px 14px" }}>
          <span style={{ fontSize: 13, color: "#fff", fontWeight: 800 }}>I am</span>
          <input
            type="number" value={ageInput}
            onChange={e => setAgeInput(e.target.value)}
            style={{ width: 52, background: "#ffffff30", border: "none", borderRadius: 8, padding: "4px 8px", fontSize: 16, fontWeight: 900, color: "#fff", textAlign: "center", outline: "none", fontFamily: "Fredoka One" }}
          />
          <span style={{ fontSize: 13, color: "#fff", fontWeight: 800 }}>years old</span>
          {ageNum > 0 && (
            <span style={{ fontSize: 11, color: "#ffffffaa", fontWeight: 700, marginLeft: "auto" }}>
              → by age {ageNum + years}
            </span>
          )}
        </div>
      </div>

      <div style={{ padding: "14px 16px 0" }}>

        {/* Inputs card */}
        <div style={{ background: "#fff", borderRadius: 20, padding: "16px", border: "2px solid #e5e7eb", marginBottom: 12 }}>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 900, color: "#6b7280", letterSpacing: 1, marginBottom: 6 }}>STARTING AMOUNT</div>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 18, fontWeight: 900, color: "#10b981", fontFamily: "Fredoka One" }}>$</span>
                <input type="number" value={startInput} onChange={e => setStartInput(e.target.value)}
                  style={{ ...inpStyle, paddingLeft: 24, fontSize: 18 }} />
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 900, color: "#6b7280", letterSpacing: 1, marginBottom: 6 }}>WEEKLY SAVINGS</div>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 18, fontWeight: 900, color: "#10b981", fontFamily: "Fredoka One" }}>$</span>
                <input type="number" value={weeklyInput} onChange={e => setWeeklyInput(e.target.value)}
                  style={{ ...inpStyle, paddingLeft: 24, fontSize: 18 }} />
              </div>
            </div>
          </div>

          {/* Quick-add weekly buttons */}
          <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
            {[5, 10, 20, 50].map(v => (
              <button key={v} onClick={() => setWeeklyInput(String(v))}
                style={{ flex: 1, background: weeklyAmt === v ? "#10b981" : "#f3f4f6", border: "none", borderRadius: 10, padding: "6px 2px", fontWeight: 900, fontSize: 12, color: weeklyAmt === v ? "#fff" : "#374151", cursor: "pointer", fontFamily: "Nunito" }}>
                ${v}/wk
              </button>
            ))}
          </div>

          {/* Rate selector */}
          <div style={{ fontSize: 11, fontWeight: 900, color: "#6b7280", letterSpacing: 1, marginBottom: 8 }}>GROWTH RATE</div>
          <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
            {RATES.map((r, i) => (
              <button key={r.label} onClick={() => setRateIdx(i)}
                style={{ flex: 1, background: rateIdx === i ? "linear-gradient(135deg,#10b981,#059669)" : "#f3f4f6", border: "none", borderRadius: 12, padding: "8px 4px", cursor: "pointer", fontFamily: "Nunito" }}>
                <div style={{ fontSize: 13, fontWeight: 900, color: rateIdx === i ? "#fff" : "#374151" }}>{r.label}</div>
                <div style={{ fontSize: 9, color: rateIdx === i ? "#d1fae5" : "#9ca3af", fontWeight: 700 }}>{r.sub.split("·")[0].trim()}</div>
              </button>
            ))}
          </div>

          {/* Horizon selector */}
          <div style={{ fontSize: 11, fontWeight: 900, color: "#6b7280", letterSpacing: 1, marginBottom: 8 }}>TIME HORIZON</div>
          <div style={{ display: "flex", gap: 6 }}>
            {HORIZONS.map(y => (
              <button key={y} onClick={() => setYears(y)}
                style={{ flex: 1, background: years === y ? "linear-gradient(135deg,#0ea5e9,#0284c7)" : "#f3f4f6", border: "none", borderRadius: 12, padding: "8px 4px", cursor: "pointer", fontFamily: "Nunito" }}>
                <div style={{ fontSize: 14, fontWeight: 900, color: years === y ? "#fff" : "#374151" }}>{y} yr</div>
              </button>
            ))}
          </div>
        </div>

        {/* Chart */}
        <div style={{ background: "#fff", borderRadius: 20, padding: "16px", border: "2px solid #e5e7eb", marginBottom: 12 }}>
          <Chart main={mainValues} compare={compareValues} years={years} />

          <div style={{ display: "flex", gap: 16, marginTop: 8, justifyContent: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 20, height: 3, background: "#10b981", borderRadius: 2 }} />
              <span style={{ fontSize: 11, fontWeight: 800, color: "#374151" }}>
                {showCompare && canCompare ? "Start now" : "Your money"}
              </span>
            </div>
            {showCompare && canCompare && (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 20, height: 3, background: "#ef4444", borderRadius: 2, borderTop: "3px dashed #ef4444" }} />
                <span style={{ fontSize: 11, fontWeight: 800, color: "#374151" }}>Wait {delayYears} years</span>
              </div>
            )}
          </div>
        </div>

        {/* Summary cards */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
          <div style={{ background: "linear-gradient(135deg,#10b981,#059669)", borderRadius: 18, padding: "14px 12px", textAlign: "center" as const }}>
            <div style={{ fontSize: 11, color: "#d1fae5", fontWeight: 800, marginBottom: 4 }}>
              {ageNum > 0 ? `BY AGE ${ageNum + years}` : `IN ${years} YEARS`}
            </div>
            <div style={{ fontSize: 24, fontWeight: 900, color: "#fff", fontFamily: "Fredoka One" }}>{fmt(finalValue)}</div>
            <div style={{ fontSize: 10, color: "#a7f3d0", fontWeight: 700, marginTop: 2 }}>total value</div>
          </div>
          <div style={{ background: "#fff", borderRadius: 18, padding: "14px 12px", textAlign: "center" as const, border: "2px solid #e5e7eb" }}>
            <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 800, marginBottom: 4 }}>YOU INVEST</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: "#1f2937", fontFamily: "Fredoka One" }}>{fmt(totalInvested)}</div>
            <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 700, marginTop: 2 }}>total contributions</div>
          </div>
          <div style={{ background: "#fff8f0", borderRadius: 18, padding: "14px 12px", textAlign: "center" as const, border: "2px solid #fed7aa", gridColumn: interestEarned > 0 ? "1" : "1 / -1" }}>
            <div style={{ fontSize: 11, color: "#9a3412", fontWeight: 800, marginBottom: 4 }}>INTEREST EARNED</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: "#ea580c", fontFamily: "Fredoka One" }}>{fmt(interestEarned)}</div>
            <div style={{ fontSize: 10, color: "#c2410c", fontWeight: 700, marginTop: 2 }}>free money from growth!</div>
          </div>
          {interestEarned > 0 && (
            <div style={{ background: "#fef9c3", borderRadius: 18, padding: "14px 12px", textAlign: "center" as const, border: "2px solid #fde68a" }}>
              <div style={{ fontSize: 11, color: "#78350f", fontWeight: 800, marginBottom: 4 }}>MULTIPLIER</div>
              <div style={{ fontSize: 24, fontWeight: 900, color: "#d97706", fontFamily: "Fredoka One" }}>
                {totalInvested > 0 ? `${(finalValue / totalInvested).toFixed(1)}×` : "—"}
              </div>
              <div style={{ fontSize: 10, color: "#92400e", fontWeight: 700, marginTop: 2 }}>return on investment</div>
            </div>
          )}
        </div>

        {/* Contributions vs Interest bar */}
        {finalValue > 0 && totalInvested > 0 && (
          <div style={{ background: "#fff", border: "2px solid #e5e7eb", borderRadius: 16, padding: "12px 14px", marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 900, color: "#374151", marginBottom: 8 }}>
              Your money vs. free growth
            </div>
            <div style={{ height: 18, borderRadius: 10, background: "#e5e7eb", overflow: "hidden", marginBottom: 6 }}>
              <div style={{ display: "flex", height: "100%" }}>
                <div style={{ width: `${100 - interestPct}%`, background: "#6b7280", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {(100 - interestPct) > 20 && <span style={{ fontSize: 9, color: "#fff", fontWeight: 900 }}>{100 - interestPct}%</span>}
                </div>
                <div style={{ width: `${interestPct}%`, background: "#10b981", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {interestPct > 15 && <span style={{ fontSize: 9, color: "#fff", fontWeight: 900 }}>{interestPct}%</span>}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: "#6b7280" }} />
                <span style={{ fontSize: 11, color: "#6b7280", fontWeight: 700 }}>You put in {fmt(totalInvested)}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: "#10b981" }} />
                <span style={{ fontSize: 11, color: "#10b981", fontWeight: 800 }}>Growth gave you {fmt(interestEarned)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Growth milestones */}
        {finalValue > 0 && (
          <GrowthMilestones
            mainValues={mainValues}
            startAmt={startAmt}
            weeklyAmt={weeklyAmt}
            years={years}
            ageNum={ageNum}
          />
        )}

        {/* Where to Put It */}
        <WhereToInvest rateIdx={rateIdx} weeklyAmt={weeklyAmt} years={years} />

        {/* Compare toggle */}
        {canCompare ? (
          <button onClick={() => setShowCompare(v => !v)}
            style={{ width: "100%", background: showCompare ? "linear-gradient(135deg,#ef4444,#dc2626)" : "#fff", border: `2.5px solid ${showCompare ? "#ef4444" : "#e5e7eb"}`, borderRadius: 16, padding: "13px 18px", color: showCompare ? "#fff" : "#374151", fontWeight: 900, fontSize: 14, cursor: "pointer", fontFamily: "Fredoka One", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 12 }}>
            <span>{showCompare ? "⏹" : "▶"}</span>
            {showCompare ? `Comparing: Start Now vs. Wait ${delayYears} Years` : `Compare: What if I waited ${delayYears} years?`}
          </button>
        ) : (
          <div style={{ background: "#f9fafb", borderRadius: 14, padding: "10px 16px", marginBottom: 12, textAlign: "center" as const }}>
            <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 700 }}>Pick 10, 20, or 30 years to unlock the comparison mode</span>
          </div>
        )}

        {/* Comparison result card */}
        {showCompare && canCompare && headStart > 0 && (
          <div style={{ background: "linear-gradient(135deg,#fef9c3,#fef3c7)", border: "2.5px solid #fbbf24", borderRadius: 18, padding: "16px", marginBottom: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 900, color: "#78350f", fontFamily: "Fredoka One", marginBottom: 6 }}>
              🚀 Starting now beats waiting by:
            </div>
            <div style={{ fontSize: 32, fontWeight: 900, color: "#d97706", fontFamily: "Fredoka One", marginBottom: 4 }}>
              {fmt(headStart)}
            </div>
            <div style={{ fontSize: 13, color: "#92400e", fontWeight: 700 }}>
              That's {fmt(headStart)} more just for starting {delayYears} years earlier.
              Compound interest rewards the early starters! 💸
            </div>
          </div>
        )}

        {/* Fun fact */}
        <div style={{ background: "#f0fdf4", border: "2px solid #bbf7d0", borderRadius: 14, padding: "12px 14px", marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 900, color: "#166534", marginBottom: 4 }}>💡 The Rule of 72</div>
          <div style={{ fontSize: 12, color: "#15803d", fontWeight: 700 }}>
            At {rate}% growth, your money doubles every <strong>{Math.round(72 / rate)} years</strong>.
            {ageNum > 0 && ` That means by age ${ageNum + Math.round(72 / rate)} you'll have ${fmt(startAmt * 2 + weeklyAmt * 52 * Math.round(72/rate))} just from savings!`}
          </div>
        </div>

      </div>
    </div>
  );
}
